import React, { FC } from 'react';
import { formatDate } from 'utils/format';
import { useTranslation } from 'react-i18next';
import { Box, TableCell, TableRow, Typography } from '@mui/material';
import TimeInStep from 'components/shared/time-in-step/time-in-step.component';
import { OrderStep } from 'models/workflow-step';
import { User } from 'models/user';

const getElapsedTime = (from: string | Date, to?: string | Date) => {
    const elapsed = (to ? new Date(to).getTime() : Date.now()) - new Date(from).getTime();
    return {
        hours: Math.floor(elapsed / 3600000),
        minutes: Math.floor((elapsed % 3600000) / 60000),
        seconds: Math.floor((elapsed % 60000) / 1000),
    };
};

const timeToMs = (t: { hours: number; minutes: number; seconds: number }) =>
    (t.hours * 3600 + t.minutes * 60 + t.seconds) * 1000;

const subtractTime = (
    time: { hours: number; minutes: number; seconds: number },
    subtract: { hours: number; minutes: number; seconds: number } | undefined,
) => {
    if (!subtract) return time;
    const ms = Math.max(0, timeToMs(time) - timeToMs(subtract));
    return {
        hours: Math.floor(ms / 3600000),
        minutes: Math.floor((ms % 3600000) / 60000),
        seconds: Math.floor((ms % 60000) / 1000),
    };
};

const getLivePauseTime = (step: OrderStep) => {
    if (!step.pauseLog?.length) return step.totalPauseTime;
    let totalMs = 0;
    for (let index = 0; index < step.pauseLog.length; index++) {
        const entry = step.pauseLog[index];
        if (entry.type === 'resume') {
            const preceding = [...step.pauseLog]
                .slice(0, index)
                .reverse()
                .find((e) => e.type === 'pause');
            if (preceding) {
                totalMs += new Date(entry.dateTime).getTime() - new Date(preceding.dateTime).getTime();
            }
        }
    }
    if (step.isPaused) {
        const lastPause = [...step.pauseLog].reverse().find((e) => e.type === 'pause');
        if (lastPause) totalMs += Date.now() - new Date(lastPause.dateTime).getTime();
    }
    return {
        hours: Math.floor(totalMs / 3600000),
        minutes: Math.floor((totalMs % 3600000) / 60000),
        seconds: Math.floor((totalMs % 60000) / 1000),
    };
};

type StepLogRowProps = { step: OrderStep };

const renderStepName = (step: OrderStep, translate: (key: string) => string) => {
    if (step.isPaused && !step.pauseLog?.length) {
        return (
            <TableCell sx={{ maxWidth: '200px' }}>
                <strong>{translate('workflow:PAUSED')}</strong> - {step.name}
            </TableCell>
        );
    }
    const name =
        step.name === 'accepted_by_lab' || step.name === 'sent_by_lab'
            ? translate(`orders:${step.name.toUpperCase()}`)
            : step.name === 'ALL_STEPS_DONE'
              ? translate('workflow:ALL_STEPS_DONE')
              : step.name === 'NO_STEP' || !step.name
                ? translate('workflow:NO_STEP')
                : step.name;
    return <TableCell sx={{ maxWidth: '200px' }}>{name}</TableCell>;
};

const renderTimeSpent = (step: OrderStep) => {
    if (step.timeSpent) {
        return <TimeInStep timeSpent={step.timeSpent} maxHourInStep={step.maxHourInStep} />;
    }
    if (step.dateTime) {
        return <TimeInStep timeSpent={getElapsedTime(step.dateTime)} maxHourInStep={step.maxHourInStep} />;
    }
    return undefined;
};

const renderTimeWaiting = (step: OrderStep) => {
    if (step.timeWaiting) {
        return <TimeInStep timeSpent={step.timeWaiting} maxHourInStep={0} />;
    }
    if (step.workStarted) {
        return <TimeInStep timeSpent={getElapsedTime(step.dateTime, step.workStarted)} maxHourInStep={0} />;
    }
    if (step.timeSpent) {
        return <TimeInStep timeSpent={step.timeSpent} maxHourInStep={0} />;
    }
    if (step.dateTime) {
        return <TimeInStep timeSpent={getElapsedTime(step.dateTime)} maxHourInStep={0} />;
    }
    return undefined;
};

const renderTimeProduction = (step: OrderStep) => {
    if (step.timeProduction) {
        return <TimeInStep timeSpent={step.timeProduction} maxHourInStep={0} />;
    }
    if (step.workStarted) {
        return (
            <TimeInStep
                timeSpent={subtractTime(getElapsedTime(step.workStarted), getLivePauseTime(step))}
                maxHourInStep={0}
            />
        );
    }
    return undefined;
};

const renderPauseTime = (step: OrderStep) => {
    const livePause = getLivePauseTime(step);
    if (!livePause) return undefined;
    return <TimeInStep timeSpent={livePause} maxHourInStep={0} />;
};

const StepLogRow: FC<StepLogRowProps> = ({ step }) => {
    const { t: translate } = useTranslation(['workflow', 'orders']);

    return (
        <TableRow>
            {renderStepName(step, translate)}
            <TableCell sx={{ width: '150px' }}>{formatDate(new Date(step.dateTime), 'datetime')}</TableCell>
            <TableCell sx={{ width: '150px' }}>
                {step.workStarted ? (
                    formatDate(new Date(step.workStarted), 'datetime')
                ) : step.name !== 'accepted_by_lab' && step.name !== 'sent_by_lab' && step.name !== 'ALL_STEPS_DONE' ? (
                    <Typography variant="body2" color="textSecondary">
                        {translate('workflow:WORK_NOT_STARTED')}
                    </Typography>
                ) : (
                    '-'
                )}
            </TableCell>
            <TableCell sx={{ maxWidth: '200px' }}>{step.user?.fullName || '-'}</TableCell>
            <TableCell>
                {step.comment}
                {step.automation ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                        {translate('workflow:AUTOMATED_BY_SUB_ORDER', {
                            orderId: step.automation.subOrder?.orderId ?? '',
                            status: translate(`orders:${step.automation.status}`),
                        })}
                    </Typography>
                ) : undefined}
            </TableCell>
            <TableCell>
                {step.externalTechnicians?.map((user: User) => <Box key={user._id}>{user.fullName}</Box>)}
            </TableCell>
            <TableCell>{renderTimeSpent(step)}</TableCell>
            <TableCell>{renderTimeWaiting(step)}</TableCell>
            <TableCell>{renderTimeProduction(step)}</TableCell>
            <TableCell>{renderPauseTime(step)}</TableCell>
        </TableRow>
    );
};

export default StepLogRow;
